import { BarcodeEvent, BarcodeType } from '../types/barcode'

// Hex strings for python3 bytes.fromhex() — more reliable than shell printf for binary data with null bytes
const CMD_ENABLE_CONTINUOUS = '020700535730303030319B0103'
const CMD_DISABLE = '02070053574646464646080203'

const DEVICE_PATHS = ['/dev/ttyACM0', '/dev/ttyUSB0']

function getNode(): {
  exec: (cmd: string) => Promise<{ stdout: string; stderr: string; error: any }>
  spawn: any
} | null {
  return (window as any)._depoto?.node ?? null
}

export class SerialBarcodeScanner {
  private devicePath: string | null = null
  private readerProcess: any = null
  private isRunning = false
  private aborted = false
  private lineBuffer = ''
  private subscribers = new Map<string, Set<(event: BarcodeEvent) => void>>()
  private lastEmittedCode = ''
  private lastEmittedAt = 0
  private static readonly DEDUP_MS = 500

  private static readonly POSITION_PATTERN = /^([a-zA-Z0-9]{1,3})[-.]([a-zA-Z0-9]{1,3})[-.]([a-zA-Z0-9]{1,3})$/

  static isAvailable(): boolean {
    const node = getNode()
    return !!(node?.exec && node?.spawn)
  }

  async start(): Promise<boolean> {
    if (this.isRunning) return true
    this.aborted = false
    const node = getNode()
    if (!node) return false

    const device = await this.findDevice(node.exec)
    if (this.aborted) return false
    if (!device) {
      console.warn('SerialBarcodeScanner: no device found at', DEVICE_PATHS.join(' or '))
      return false
    }
    this.devicePath = device

    await node.exec(`stty -F ${device} 9600 cs8 -cstopb -parenb raw -echo 2>/dev/null || true`)
    if (this.aborted) return false
    await this.sendCommand(node.exec, CMD_ENABLE_CONTINUOUS)
    if (this.aborted) return false

    // stdbuf -i0 -o0 disables cat's internal block-buffering so each barcode
    // is forwarded immediately instead of sitting in an 8 KB stdio buffer.
    const hasStdbuf = await node
      .exec('command -v stdbuf')
      .then((r: any) => !!r?.stdout?.trim())
      .catch(() => false)
    if (this.aborted) return false

    if (hasStdbuf) {
      this.readerProcess = node.spawn('stdbuf', ['-i0', '-o0', 'cat', device])
    } else {
      this.readerProcess = node.spawn('cat', [device])
    }

    if (this.aborted) {
      this.readerProcess.kill()
      this.readerProcess = null
      return false
    }
    this.isRunning = true

    this.readerProcess.stdout.on('data', (data: Buffer) => {
      // Strip non-printable bytes except CR/LF before buffering
      this.lineBuffer += data.toString('latin1').replace(/[^\x20-\x7E\r\n]/g, '')
      const lines = this.lineBuffer.split(/[\r\n]+/)
      this.lineBuffer = lines.pop() ?? ''
      for (const line of lines) {
        const code = line.trim()
        if (code.length >= 3) this.handleCode(code)
      }
    })

    this.readerProcess.on('exit', () => {
      this.isRunning = false
      this.readerProcess = null
    })

    return true
  }

  async stop(): Promise<void> {
    // Set aborted first — this cancels any in-flight start() that hasn't set isRunning yet
    this.aborted = true
    if (!this.isRunning) return
    this.isRunning = false
    this.readerProcess?.kill()
    this.readerProcess = null
    const node = getNode()
    if (node && this.devicePath) {
      await this.sendCommand(node.exec, CMD_DISABLE).catch(() => null)
    }
  }

  private async findDevice(exec: (cmd: string) => Promise<{ stdout: string }>) {
    for (const path of DEVICE_PATHS) {
      const res = await exec(`test -c ${path} && echo ok`).catch(() => ({ stdout: '' }))
      if (res?.stdout?.includes('ok')) return path
    }
    return null
  }

  private async sendCommand(exec: (cmd: string) => Promise<any>, hexStr: string) {
    if (!this.devicePath) return
    // python3 is reliable for binary writes; printf struggles with null bytes in some shells
    await exec(
      `python3 -c "import sys; sys.stdout.buffer.write(bytes.fromhex('${hexStr}'))" > ${this.devicePath} 2>/dev/null || true`,
    ).catch(() => null)
  }

  private handleCode(code: string): void {
    if (!this.isRunning) return
    const now = Date.now()
    if (code === this.lastEmittedCode && now - this.lastEmittedAt < SerialBarcodeScanner.DEDUP_MS) return
    this.lastEmittedCode = code
    this.lastEmittedAt = now

    const event: BarcodeEvent = {
      type: this.detectType(code),
      data: code,
      timestamp: Date.now(),
      raw: code,
    }
    this.emit(event)
  }

  private detectType(code: string): BarcodeType {
    if (SerialBarcodeScanner.POSITION_PATTERN.test(code)) return 'POSITION'
    if (/^(\(01\)|01)\d{14}/.test(code)) return 'GS1'
    if (code.includes('http') || code.includes('://')) return 'QR'
    return 'BARCODE'
  }

  subscribe(id: string, callback: (event: BarcodeEvent) => void): void {
    if (!this.subscribers.has(id)) this.subscribers.set(id, new Set())
    this.subscribers.get(id)!.add(callback)
  }

  unsubscribe(id: string, callback: (event: BarcodeEvent) => void): void {
    const set = this.subscribers.get(id)
    if (set) {
      set.delete(callback)
      if (set.size === 0) this.subscribers.delete(id)
    }
  }

  private emit(event: BarcodeEvent): void {
    this.subscribers.forEach(set =>
      set.forEach(cb => {
        try {
          cb(event)
        } catch (e) {
          console.error('SerialBarcodeScanner subscriber error', e)
        }
      }),
    )
  }
}
